import { AuthService } from './auth'

// Odoo CRM API base URL
const ODOO_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://api.quali-d.com'
  : 'https://api.quali-d.com'

export class OdooService {
  static async makeAuthenticatedRequest (endpoint, options = {}) {
    const token = AuthService.getAuthToken()

    if (!token) {
      throw new Error('No authentication token found')
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers
    }

    const response = await fetch(`${ODOO_API_BASE_URL}${endpoint}`, {
      ...options,
      headers
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP ${response.status}`)
    }

    return response.json()
  }

  // Check Odoo integration status
  static async getStatus () {
    try {
      return await this.makeAuthenticatedRequest('/crm/odoo/status')
    } catch (error) {
      console.error('Error fetching Odoo status:', error)
      return {
        connected: false,
        error: error.message
      }
    }
  }

  // Test Odoo connection without saving
  static async testConnection (credentials) {
    try {
      const queryParams = new URLSearchParams({
        odoo_url: credentials.odoo_url,
        database: credentials.database,
        username: credentials.username,
        password: credentials.password
      })

      return await this.makeAuthenticatedRequest(`/crm/odoo/test-connection?${queryParams}`)
    } catch (error) {
      console.error('Error testing Odoo connection:', error)
      throw error
    }
  }

  // Connect to Odoo CRM
  static async connect (credentials) {
    try {
      return await this.makeAuthenticatedRequest('/crm/odoo/connect', {
        method: 'POST',
        body: JSON.stringify(credentials)
      })
    } catch (error) {
      console.error('Error connecting to Odoo:', error)
      throw error
    }
  }

  // Disconnect Odoo integration
  static async disconnect () {
    try {
      return await this.makeAuthenticatedRequest('/crm/odoo/disconnect', {
        method: 'POST'
      })
    } catch (error) {
      console.error('Error disconnecting Odoo:', error)
      throw error
    }
  }

  // Update sync settings
  static async updateSettings (settings) {
    try {
      return await this.makeAuthenticatedRequest('/crm/odoo/settings', {
        method: 'PUT',
        body: JSON.stringify(settings)
      })
    } catch (error) {
      console.error('Error updating Odoo settings:', error)
      throw error
    }
  }

  // Get sync history
  static async getSyncHistory (params = {}) {
    try {
      const queryParams = new URLSearchParams()

      if (params.page) queryParams.append('page', params.page)
      if (params.per_page) queryParams.append('per_page', params.per_page)
      if (params.sync_type) queryParams.append('sync_type', params.sync_type)
      if (params.success !== undefined) queryParams.append('success', params.success)

      const query = queryParams.toString()
      const endpoint = query ? `/crm/odoo/sync-logs?${query}` : '/crm/odoo/sync-logs'

      return await this.makeAuthenticatedRequest(endpoint)
    } catch (error) {
      console.error('Error fetching Odoo sync history:', error)
      throw error
    }
  }

  // Manual sync call analysis
  static async syncCallAnalysis (analysisData) {
    try {
      return await this.makeAuthenticatedRequest('/crm/odoo/sync/call-analysis', {
        method: 'POST',
        body: JSON.stringify(analysisData)
      })
    } catch (error) {
      console.error('Error syncing call analysis to Odoo:', error)
      throw error
    }
  }

  // Manual sync WhatsApp lead
  static async syncWhatsAppLead (leadData) {
    try {
      return await this.makeAuthenticatedRequest('/crm/odoo/sync/whatsapp-lead', {
        method: 'POST',
        body: JSON.stringify(leadData)
      })
    } catch (error) {
      console.error('Error syncing WhatsApp lead to Odoo:', error)
      throw error
    }
  }

  // Get available Odoo modules
  static async getModules () {
    try {
      return await this.makeAuthenticatedRequest('/crm/odoo/modules')
    } catch (error) {
      console.error('Error fetching Odoo modules:', error)
      throw error
    }
  }

  // Get model fields for a specific Odoo model
  static async getModelFields (model) {
    try {
      return await this.makeAuthenticatedRequest(`/crm/odoo/fields/${model}`)
    } catch (error) {
      console.error(`Error fetching fields for model ${model}:`, error)
      throw error
    }
  }

  // Sync specific appointment
  static async syncAppointment (appointmentId) {
    try {
      return await this.makeAuthenticatedRequest(`/crm/odoo/appointments/${appointmentId}/sync`, {
        method: 'POST'
      })
    } catch (error) {
      console.error('Error syncing appointment to Odoo:', error)
      throw error
    }
  }

  // Get appointment sync status
  static async getAppointmentSyncStatus () {
    try {
      return await this.makeAuthenticatedRequest('/crm/odoo/appointments/sync-status')
    } catch (error) {
      console.error('Error fetching appointment sync status:', error)
      throw error
    }
  }

  // Update appointment status
  static async updateAppointmentStatus (appointmentId, statusData) {
    try {
      return await this.makeAuthenticatedRequest(`/crm/odoo/appointments/${appointmentId}/status`, {
        method: 'PUT',
        body: JSON.stringify(statusData)
      })
    } catch (error) {
      console.error('Error updating appointment status:', error)
      throw error
    }
  }

  // Helper method to format sync status for display
  static formatSyncStatus (status) {
    if (!status) return { text: 'Unknown', color: 'gray', icon: '?' }

    if (status.connected) {
      if (!status.connection_valid) {
        return { text: 'Connection Invalid', color: 'red', icon: '⚠️' }
      }
      return { text: 'Connected', color: 'green', icon: '✓' }
    }

    return { text: 'Disconnected', color: 'gray', icon: '○' }
  }

  // Helper method to format last sync time
  static formatLastSync (timestamp) {
    if (!timestamp) return 'Never'

    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return date.toLocaleDateString()
  }
}
